import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/Icon';
import { uploadChatFile } from '../../api';
import useVoiceRecorder from '../useVoiceRecorder';
import { formatDuration, messagePreview } from '../chatUtils';
import { MAX_VOICE_DURATION_MS } from '../chatConstants';

const EMOJIS = ['🙂', '😀', '😉', '😍', '🤝', '👍', '🙏', '🔥', '✅', '❗', '💬', '📎'];

const attachmentTypeOf = (file) => {
  if (file.type.startsWith('image/')) return 'IMAGE';
  return 'FILE';
};

/**
 * Поле ввода: текст, прикрепление файла (всегда два шага — сначала upload,
 * потом сообщение с fileId), эмодзи и запись голосового.
 */
const MessageComposer = ({
  chatId, replyTo, onCancelReply, onSend, onTyping, disabled, placeholderName,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const { recording, elapsedMs, start, stop, cancel, error: recorderError } = useVoiceRecorder();

  const resetAfterSend = () => {
    setText('');
    onCancelReply?.();
  };

  const handleSendText = async () => {
    const content = text.trim();
    if (!content || disabled) return;
    await onSend({ content, replyToId: replyTo?.id });
    resetAfterSend();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendText();
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError(null);
    setUploadProgress(0);
    try {
      const uploaded = await uploadChatFile(file, { onProgress: setUploadProgress });
      await onSend({
        fileId: uploaded.id,
        content: text.trim() || undefined,
        replyToId: replyTo?.id,
        messageType: attachmentTypeOf(file),
        localPreview: {
          id: uploaded.id,
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
          url: URL.createObjectURL(file),
        },
      });
      resetAfterSend();
    } catch (e) {
      setError(t('chat.composer.uploadError'));
    } finally {
      setUploadProgress(null);
    }
  };

  const finishRecording = async () => {
    const result = await stop();
    if (!result) return;

    setUploadProgress(0);
    try {
      const uploaded = await uploadChatFile(result.file, { onProgress: setUploadProgress });
      await onSend({
        fileId: uploaded.id,
        replyToId: replyTo?.id,
        // durationMs обязателен: без него бэкенд сохранит запись как обычный файл.
        durationMs: result.durationMs,
        waveform: result.waveform,
        messageType: 'VOICE',
      });
      resetAfterSend();
    } catch {
      setError(t('chat.composer.voiceError'));
    } finally {
      setUploadProgress(null);
    }
  };

  return (
    <div className="chat-composer">
      {replyTo && (
        <div className="chat-composer-reply">
          <span className="chat-reply-quote-bar" />
          <span className="chat-composer-reply-text">{messagePreview(replyTo)}</span>
          <button type="button" onClick={onCancelReply} title={t('chat.composer.cancelReply')}>
            <Icon name="x" size={14} />
          </button>
        </div>
      )}

      {uploadProgress != null && (
        <div className="chat-upload">
          <span className="chat-upload-bar">
            <span className="chat-upload-fill" style={{ width: `${uploadProgress}%` }} />
          </span>
          <span className="chat-upload-value">{uploadProgress}%</span>
        </div>
      )}

      {(error || recorderError) && (
        <div className="chat-composer-error">{error || recorderError}</div>
      )}

      {recording ? (
        <div className="chat-recording">
          <span className="chat-recording-dot" />
          <span className="chat-recording-time">
            {formatDuration(elapsedMs)} / {formatDuration(MAX_VOICE_DURATION_MS)}
          </span>
          <button type="button" className="chat-icon-btn" onClick={cancel} title={t('chat.composer.recordCancel')}>
            <Icon name="trash-2" size={18} />
          </button>
          <button
            type="button"
            className="chat-send-btn"
            onClick={finishRecording}
            title={t('chat.composer.recordSend')}
          >
            <Icon name="send" size={18} />
          </button>
        </div>
      ) : (
        <div className="chat-composer-row">
          <button
            type="button"
            className="chat-icon-btn"
            onClick={() => fileInputRef.current?.click()}
            title={t('chat.composer.attach')}
            disabled={disabled}
          >
            <Icon name="paperclip" size={18} />
          </button>
          <input ref={fileInputRef} type="file" hidden onChange={handleFile} />

          <div className="chat-emoji-wrap">
            <button
              type="button"
              className="chat-icon-btn"
              onClick={() => setEmojiOpen((v) => !v)}
              title={t('chat.composer.emoji')}
              disabled={disabled}
            >
              <Icon name="smile" size={18} />
            </button>
            {emojiOpen && (
              <div className="chat-emoji-picker">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => {
                      setText((prev) => prev + emoji);
                      setEmojiOpen(false);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          <textarea
            className="chat-input"
            value={text}
            rows={1}
            placeholder={placeholderName
              ? t('chat.composer.placeholderNamed', { name: placeholderName })
              : t('chat.composer.placeholder')}
            disabled={disabled}
            onChange={(e) => {
              setText(e.target.value);
              onTyping?.();
            }}
            onKeyDown={handleKeyDown}
          />

          {text.trim() ? (
            <button type="button" className="chat-send-btn" onClick={handleSendText} title={t('chat.composer.send')}>
              <Icon name="send" size={18} />
            </button>
          ) : (
            <button
              type="button"
              className="chat-icon-btn"
              onClick={start}
              title={t('chat.composer.voice')}
              disabled={disabled || !chatId}
            >
              <Icon name="mic" size={18} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageComposer;
